import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Moon, Sparkles, User, MessageSquare, Info, Download, Trash2, Shield,
  Smartphone, ChevronRight, LogOut, RefreshCw, FileText, CalendarClock,
  Palette, Target, Bookmark
} from 'lucide-react';
import { AppHeader } from '@/components/Layout/AppHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useDarkMode } from '@/hooks/useDarkMode';
import { useHapticFeedback } from '@/hooks/useHapticFeedback';
import { FeedbackForm } from '@/components/Settings/FeedbackForm';
import { SavedExpensesManager } from '@/components/Settings/SavedExpensesManager';
import { RecurringExpensesManager } from '@/components/Recurring/RecurringExpensesManager';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { getVersionString } from '@/lib/version';
import { CoachmarkSheet } from '@/components/Gamification/CoachmarkSheet';
import type { MotivationStyle } from '@/contexts/GamificationContext';
import { useGamification } from '@/contexts/GamificationContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from '@/lib/i18n';
import { usePWA } from '@/contexts/PWAContext';
import { useWhatsNew } from '@/hooks/useWhatsNew';
import { useVersionCheck } from '@/hooks/useVersionCheck';
import { checkAndAwardBadgesForEvent } from '@/lib/badges';
import { getUserProgress } from '@/lib/gamification';

const currencies = ['EUR', 'USD', 'GBP', 'JPY', 'CHF', 'CAD', 'AUD', 'CNY'];
const SHOW_LANGUAGE_SELECTOR = false;

// ─── Collapsible Section Component ───────────────────────────────────────────
interface CollapsibleSectionProps {
  icon: React.ReactNode;
  title: string;
  sectionKey: string;
  openSection: string | null;
  onToggle: (key: string) => void;
  children: React.ReactNode;
}

function CollapsibleSection({ icon, title, sectionKey, openSection, onToggle, children }: CollapsibleSectionProps) {
  const isOpen = openSection === sectionKey;

  return (
    <div className="border rounded-xl bg-card overflow-hidden">
      <button
        onClick={() => onToggle(sectionKey)}
        className="w-full flex items-center justify-between p-4 hover:bg-accent/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          {icon}
          <span className="font-semibold text-base">{title}</span>
        </div>
        <motion.div
          animate={{ rotate: isOpen ? 90 : 0 }}
          transition={{ type: "spring", stiffness: 200, damping: 20 }}
        >
          <ChevronRight className="h-5 w-5 text-muted-foreground" />
        </motion.div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 space-y-3">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Toggle Row Component ────────────────────────────────────────────────────
interface ToggleRowProps {
  id: string;
  label: string;
  description?: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  icon?: React.ReactNode;
}

function ToggleRow({ id, label, description, checked, onCheckedChange, icon }: ToggleRowProps) {
  return (
    <div className="flex items-center justify-between py-3">
      <div className="flex-1">
        <Label htmlFor={id} className="text-base cursor-pointer flex items-center gap-2">
          {icon}
          {label}
        </Label>
        {description && (
          <p className="text-sm text-muted-foreground mt-0.5">{description}</p>
        )}
      </div>
      <Switch
        id={id}
        checked={checked}
        onCheckedChange={onCheckedChange}
        className="scale-110"
      />
    </div>
  );
}

// ─── Nav Row Component ───────────────────────────────────────────────────────
interface NavRowProps {
  icon: React.ReactNode;
  label: string;
  description?: string;
  onClick: () => void;
}

function NavRow({ icon, label, description, onClick }: NavRowProps) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center justify-between py-3 hover:bg-accent/30 -mx-1 px-1 rounded-lg transition-colors"
    >
      <div className="flex items-center gap-2">
        {icon}
        <div className="text-left">
          <span className="text-sm font-medium">{label}</span>
          {description && (
            <p className="text-xs text-muted-foreground">{description}</p>
          )}
        </div>
      </div>
      <ChevronRight className="h-4 w-4 text-muted-foreground" />
    </button>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// MAIN SETTINGS COMPONENT
// ═════════════════════════════════════════════════════════════════════════════
export default function Settings() {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { isDark, toggleDark } = useDarkMode();
  const { light } = useHapticFeedback();
  const { settings: gamificationSettings, updateSettings: updateGamificationSettings } = useGamification();
  const { language, setLanguage, t } = useLanguage();
  const { isInstalled } = usePWA();
  const { hasNewVersion, dismissVersion } = useWhatsNew();
  const { forceCheck } = useVersionCheck();
  const [isCheckingUpdates, setIsCheckingUpdates] = useState(false);

  const [budget, setBudget] = useState('1000');
  const [currency, setCurrency] = useState('EUR');
  const [name, setName] = useState('');
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [coachmarkOpen, setCoachmarkOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [openSection, setOpenSection] = useState<string | null>(null);

  const calmMode = gamificationSettings?.hideRewards ?? false;

  useEffect(() => { loadSettings(); }, []);

  useEffect(() => {
    if (location.state?.openFeedback) {
      setFeedbackOpen(true);
      setOpenSection('support');
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const toggleSection = (key: string) => {
    setOpenSection(prev => prev === key ? null : key);
  };

  // ─── Data Loading ──────────────────────────────────────────────────────────
  const loadSettings = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data } = await supabase
        .from('budget_settings')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (data) {
        setBudget(data.monthly_budget.toString());
        setCurrency(data.currency);
      }

      const userName = localStorage.getItem('userName');
      if (userName) setName(userName);
    } catch (error) {
      console.error('Load settings error:', error);
    }
  };

  // ─── Auto-save Handlers ───────────────────────────────────────────────────
  const autoSaveSetting = async (field: 'budget' | 'currency' | 'name', value: any) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      if (field === 'budget' || field === 'currency') {
        await supabase.from('budget_settings').upsert({
          user_id: user.id,
          monthly_budget: field === 'budget' ? parseFloat(value) : parseFloat(budget),
          currency: field === 'currency' ? value : currency,
        });
      }

      if (field === 'name' && value) {
        localStorage.setItem('userName', value);
      }

      if (!calmMode) light();
      toast({ description: t('toast.saved'), duration: 1500 });
    } catch (error) {
      console.error('Auto-save error:', error);
      toast({ title: "Save failed", duration: 1500 });
    }
  };

  const handleBudgetBlur = () => {
    if (budget && parseFloat(budget) > 0) autoSaveSetting('budget', budget);
  };

  const handleNameBlur = async () => {
    if (name) {
      await autoSaveSetting('name', name);
      // Check for 'named' badge
      const { data: { user } } = await supabase.auth.getUser();
      if (user && name.trim().length > 0) {
        const progress = await getUserProgress(user.id);
        await checkAndAwardBadgesForEvent(user.id, 'profile_saved', progress);
      }
    }
  };

  const handleCurrencyChange = (value: string) => {
    setCurrency(value);
    autoSaveSetting('currency', value);
  };

  const handleDarkModeToggle = () => {
    toggleDark();
    if (!calmMode) light();
    toast({ description: t('toast.saved'), duration: 1500 });
  };

  const handleLanguageChange = async (value: SupportedLanguage) => {
    await setLanguage(value);
    if (!calmMode) light();
    toast({ description: t('toast.languageChanged'), duration: 1500 });
  };

  const handleMotivationStyleChange = async (value: string) => {
    try {
      await updateGamificationSettings({ style: value as MotivationStyle });
      if (!calmMode) light();
      toast({ description: t('toast.saved'), duration: 1500 });

      // Check for found_your_style badge
      if (value !== 'gentle') {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const progress = await getUserProgress(user.id);
          await checkAndAwardBadgesForEvent(user.id, 'motivation_style_changed', progress);
        }
      }
    } catch (error) {
      console.error('Update motivation style error:', error);
    }
  };

  const handleShowCelebrationsToggle = async (checked: boolean) => {
    try {
      await updateGamificationSettings({ showCelebrations: checked });
      if (!calmMode) light();
      toast({ description: t('toast.saved'), duration: 1500 });
    } catch (error) {
      console.error('Update show celebrations error:', error);
    }
  };

  const handleDailyNudgeToggle = async (checked: boolean) => {
    try {
      await updateGamificationSettings({ dailyNudgeEnabled: checked });
      if (!calmMode) light();
      toast({ description: t('toast.saved'), duration: 1500 });
    } catch (error) {
      console.error('Update daily nudge error:', error);
    }
  };

  const handleCalmModeToggle = async (checked: boolean) => {
    try {
      await updateGamificationSettings({ hideRewards: checked });

      // Track calm_mode_enabled_since for calm_achiever badge
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase.from('budget_settings').update({
          calm_mode_enabled_since: checked ? new Date().toISOString() : null,
        }).eq('user_id', user.id);

        if (checked) {
          const progress = await getUserProgress(user.id);
          await checkAndAwardBadgesForEvent(user.id, 'calm_mode_toggled', progress);
        }
      }

      if (!calmMode) light();
      toast({ description: t('toast.saved'), duration: 1500 });
    } catch (error) {
      console.error('Update calm mode error:', error);
    }
  };

  const handleCoachmarkDismiss = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      await supabase.from('budget_settings').update({ coachmark_points_seen: true }).eq('user_id', user.id);
      setCoachmarkOpen(false);
    } catch (error) {
      console.error('Update coachmark error:', error);
    }
  };

  const handleCoachmarkHidePoints = async () => {
    try {
      await updateGamificationSettings({ hideRewards: true, coachmarkSeen: true });
      setCoachmarkOpen(false);
      toast({ title: "Points hidden", description: "You can re-enable them anytime in Settings" });
    } catch (error) {
      console.error('Hide points error:', error);
    }
  };

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    try {
      const { data, error } = await supabase.functions.invoke('delete-account');
      if (error) throw error;
      await supabase.auth.signOut();
      toast({
        title: "Account Deleted",
        description: "Your account and all data have been permanently deleted.",
        duration: 5000,
      });
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Delete account error:', error);
      toast({
        title: "Deletion Failed",
        description: "Failed to delete your account. Please try again or contact support@trywren.app.",
        variant: "destructive",
        duration: 5000,
      });
      setIsDeleting(false);
      setDeleteDialogOpen(false);
      setConfirmText('');
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/auth');
  };

  // ═════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═════════════════════════════════════════════════════════════════════════
  return (
    <div className="min-h-screen bg-background">
      <AppHeader showDrawerTrigger={true} title={t('settings.title')} />

      <div className="container mx-auto px-6 pt-20 max-w-2xl pb-24 space-y-3">

        {/* ═══ PROFILE — Always expanded ═══ */}
        <section className="border rounded-xl bg-card p-4 space-y-4">
          <div className="flex items-center gap-3">
            <User className="h-5 w-5 text-primary" />
            <span className="font-semibold text-base">{t('settings.profile')}</span>
          </div>

          {/* Name */}
          <div className="space-y-1.5">
            <Label htmlFor="name">{t('settings.name')}</Label>
            <Input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={handleNameBlur}
              placeholder={t('settings.name')}
              className="text-base min-h-touch"
            />
          </div>

          {/* Budget + Currency inline */}
          <div className="flex gap-3">
            <div className="flex-1 space-y-1.5">
              <Label htmlFor="budget">{t('settings.monthlyBudget')}</Label>
              <Input
                id="budget"
                type="number"
                value={budget}
                onChange={(e) => setBudget(e.target.value)}
                onBlur={handleBudgetBlur}
                className="text-base min-h-touch"
              />
            </div>
            <div className="w-24 space-y-1.5">
              <Label htmlFor="currency">{t('settings.currency')}</Label>
              <Select value={currency} onValueChange={handleCurrencyChange}>
                <SelectTrigger id="currency" className="min-h-touch">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {currencies.map((cur) => (
                    <SelectItem key={cur} value={cur}>{cur}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {SHOW_LANGUAGE_SELECTOR && (
            <div className="space-y-1.5">
              <Label htmlFor="language">{t('settings.language')}</Label>
              <Select value={language} onValueChange={handleLanguageChange}>
                <SelectTrigger id="language" className="min-h-touch">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(SUPPORTED_LANGUAGES).map(([code, { name, flag }]) => (
                    <SelectItem key={code} value={code}>{flag} {name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </section>

        {/* ═══ APPEARANCE ═══ */}
        <CollapsibleSection
          icon={<Palette className="h-5 w-5 text-primary" />}
          title={t('settings.appearance')}
          sectionKey="appearance"
          openSection={openSection}
          onToggle={toggleSection}
        >
          <ToggleRow
            id="dark-mode"
            label={t('settings.darkMode')}
            description={t('settings.darkModeDesc')}
            checked={isDark}
            onCheckedChange={handleDarkModeToggle}
            icon={<Moon className="h-4 w-4" />}
          />
        </CollapsibleSection>

        {/* ═══ EXPERIENCE ═══ */}
        <CollapsibleSection
          icon={<Target className="h-5 w-5 text-primary" />}
          title={t('settings.experience')}
          sectionKey="experience"
          openSection={openSection}
          onToggle={toggleSection}
        >
          <ToggleRow
            id="calm-mode"
            label={t('settings.calmMode')}
            description={t('settings.calmModeDesc')}
            checked={gamificationSettings.hideRewards}
            onCheckedChange={handleCalmModeToggle}
          />
          <ToggleRow
            id="show-celebrations"
            label={t('settings.showCelebrations')}
            description={t('settings.showCelebrationsDesc')}
            checked={gamificationSettings.showCelebrations}
            onCheckedChange={handleShowCelebrationsToggle}
          />
          <ToggleRow
            id="daily-nudge"
            label={t('settings.dailyNudge')}
            description={t('settings.dailyNudgeDesc')}
            checked={gamificationSettings.dailyNudgeEnabled}
            onCheckedChange={handleDailyNudgeToggle}
          />

          {/* Motivation Style */}
          <div className="pt-2 space-y-3">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary" />
              <Label className="text-base">{t('settings.motivationStyle')}</Label>
            </div>
            <RadioGroup value={gamificationSettings.style} onValueChange={handleMotivationStyleChange}>
              <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-accent/50">
                <RadioGroupItem value="minimal" id="minimal" />
                <Label htmlFor="minimal" className="flex-1 cursor-pointer">
                  <div className="font-medium">{t('settings.motivationMinimal')}</div>
                  <div className="text-sm text-muted-foreground">{t('settings.motivationMinimalDesc')}</div>
                </Label>
              </div>
              <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-accent/50">
                <RadioGroupItem value="gentle" id="gentle" />
                <Label htmlFor="gentle" className="flex-1 cursor-pointer">
                  <div className="font-medium">{t('settings.motivationGentle')}</div>
                  <div className="text-sm text-muted-foreground">{t('settings.motivationGentleDesc')}</div>
                </Label>
              </div>
              <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-accent/50">
                <RadioGroupItem value="gamified" id="gamified" />
                <Label htmlFor="gamified" className="flex-1 cursor-pointer">
                  <div className="font-medium">{t('settings.motivationGamified')}</div>
                  <div className="text-sm text-muted-foreground">{t('settings.motivationGamifiedDesc')}</div>
                </Label>
              </div>
            </RadioGroup>
            <Button
              variant="link"
              className="w-full justify-start p-0 h-auto text-muted-foreground"
              onClick={() => setCoachmarkOpen(true)}
            >
              <Info className="mr-2 h-4 w-4" />
              {t('settings.whatArePoints')}
            </Button>
          </div>
        </CollapsibleSection>

        {/* ═══ RECURRING EXPENSES ═══ */}
        <CollapsibleSection
          icon={<CalendarClock className="h-5 w-5 text-primary" />}
          title="Recurring Expenses"
          sectionKey="recurring"
          openSection={openSection}
          onToggle={toggleSection}
        >
          <RecurringExpensesManager />
        </CollapsibleSection>

        {/* ═══ SAVED EXPENSES ═══ */}
        <CollapsibleSection
          icon={<Bookmark className="h-5 w-5 text-primary" />}
          title="Saved Expenses"
          sectionKey="saved"
          openSection={openSection}
          onToggle={toggleSection}
        >
          <SavedExpensesManager />
        </CollapsibleSection>

        {/* ─── Divider ─── */}
        <div className="border-t border-border/50 my-1" />

        {/* ═══ SUPPORT & HELP ═══ */}
        <CollapsibleSection
          icon={<MessageSquare className="h-5 w-5 text-primary" />}
          title="Support & Help"
          sectionKey="support"
          openSection={openSection}
          onToggle={toggleSection}
        >
          {/* What's New (moved here from top banner) */}
          {hasNewVersion && (
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium">{t('whatsNew.title')}</span>
              </div>
              <Button variant="outline" size="sm" onClick={dismissVersion}>
                {t('whatsNew.gotIt')}
              </Button>
            </div>
          )}

          {/* Feedback */}
          <div>
            <button
              onClick={() => setFeedbackOpen(!feedbackOpen)}
              className="w-full flex items-center justify-between py-3"
            >
              <div className="flex items-center gap-2">
                <MessageSquare className="h-4 w-4" />
                <span className="text-sm font-medium">{t('settings.sendFeedback')}</span>
              </div>
              <motion.div animate={{ rotate: feedbackOpen ? 90 : 0 }} transition={{ type: "spring", stiffness: 200, damping: 20 }}>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </motion.div>
            </button>
            <AnimatePresence>
              {feedbackOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <FeedbackForm />
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Install */}
          <div className="flex items-center justify-between py-3">
            <div className="flex items-center gap-2">
              <Smartphone className="h-4 w-4" />
              <span className="text-sm font-medium">
                {isInstalled ? t('settings.app.installed') : t('settings.app.install')}
              </span>
            </div>
            <Button variant="outline" size="sm" onClick={() => navigate('/install')}>
              {t('settings.app.viewInstructions')}
            </Button>
          </div>

          {/* Check for Updates */}
          <button
            className="w-full flex items-center gap-2 py-3 text-sm font-medium disabled:opacity-50"
            disabled={isCheckingUpdates}
            onClick={async () => {
              setIsCheckingUpdates(true);
              try {
                const hasUpdate = await forceCheck();
                if (!hasUpdate) {
                  toast({ description: t('settings.app.upToDate'), duration: 2000 });
                }
              } finally {
                setIsCheckingUpdates(false);
              }
            }}
          >
            <RefreshCw className={`h-4 w-4 ${isCheckingUpdates ? 'animate-spin' : ''}`} />
            {t('settings.app.checkUpdates')}
          </button>
        </CollapsibleSection>

        {/* ═══ PRIVACY & DATA ═══ */}
        <CollapsibleSection
          icon={<Shield className="h-5 w-5 text-primary" />}
          title={t('settings.privacyData')}
          sectionKey="privacy"
          openSection={openSection}
          onToggle={toggleSection}
        >
          <NavRow
            icon={<Shield className="h-4 w-4" />}
            label={t('settings.privacySettings')}
            onClick={() => navigate('/settings/privacy-analytics')}
          />
          <NavRow
            icon={<Download className="h-4 w-4" />}
            label={t('settings.downloadData')}
            onClick={() => navigate('/data-export')}
          />
        </CollapsibleSection>

        {/* ═══ LEGAL ═══ */}
        <CollapsibleSection
          icon={<FileText className="h-5 w-5 text-primary" />}
          title="Legal"
          sectionKey="legal"
          openSection={openSection}
          onToggle={toggleSection}
        >
          <NavRow
            icon={<FileText className="h-4 w-4" />}
            label="Privacy Policy"
            onClick={() => navigate('/privacy')}
          />
          <NavRow
            icon={<FileText className="h-4 w-4" />}
            label="Terms & Conditions"
            onClick={() => navigate('/terms')}
          />
          <NavRow
            icon={<FileText className="h-4 w-4" />}
            label="Imprint / Contact"
            onClick={() => navigate('/imprint')}
          />
        </CollapsibleSection>

        {/* ─── Divider ─── */}
        <div className="border-t border-border/50 my-1" />

        {/* ═══ ACCOUNT ACTIONS — Neutral, no red ═══ */}
        <section className="space-y-2">
          <Button
            onClick={() => setDeleteDialogOpen(true)}
            variant="ghost"
            className="w-full min-h-touch justify-start text-muted-foreground hover:text-foreground"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            {t('settings.deleteAccount')}
          </Button>
          <Button
            onClick={handleSignOut}
            variant="ghost"
            className="w-full min-h-touch justify-start text-muted-foreground hover:text-foreground"
          >
            <LogOut className="mr-2 h-4 w-4" />
            {t('settings.signOut')}
          </Button>
        </section>

        {import.meta.env.DEV && (
          <div className="pt-2 border-t">
            <Button
              variant="destructive"
              onClick={() => { throw new Error("Highlight test error"); }}
              className="w-full"
            >
              🧪 Test Error Tracking
            </Button>
          </div>
        )}

        <footer className="text-xs text-muted-foreground text-center py-4">
          {getVersionString()}
        </footer>
      </div>

      <CoachmarkSheet
        open={coachmarkOpen}
        onOpenChange={setCoachmarkOpen}
        onDismiss={handleCoachmarkDismiss}
        onHidePoints={handleCoachmarkHidePoints}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('settings.deleteConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p className="font-semibold text-destructive">
                This action cannot be undone.
              </p>
              <p>This will permanently delete:</p>
              <ul className="list-disc list-inside text-sm space-y-1">
                <li>All your expenses and notes</li>
                <li>Progress, badges, and streaks</li>
                <li>Settings and preferences</li>
                <li>Your account and authentication data</li>
              </ul>
              <div className="pt-2">
                <Label htmlFor="confirm-delete">Type DELETE MY ACCOUNT to proceed:</Label>
                <Input
                  id="confirm-delete"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="DELETE MY ACCOUNT"
                  className="mt-2"
                  disabled={isDeleting}
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setConfirmText('')} disabled={isDeleting}>
              {t('settings.deleteConfirmCancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={confirmText !== 'DELETE MY ACCOUNT' || isDeleting}
              onClick={handleDeleteAccount}
              className="bg-destructive hover:bg-destructive/90"
            >
              {isDeleting ? 'Deleting...' : t('settings.deleteConfirmButton')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
